import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

interface Role {
  id: number;
  name: string;
  guard_name: string;
}

interface Permission {
  id: number;
  name: string;
  guard_name: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  email_verified_at?: string;
  created_at: string;
  updated_at: string;
  roles?: Role[] | string[];
  permissions?: Permission[] | string[];
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login: (
      state,
      action: PayloadAction<{
        user: User;
        accessToken: string;
        refreshToken: string;
      }>
    ) => {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
    },
    refreshToken: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken?: string }>
    ) => {
      state.accessToken = action.payload.accessToken;
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
      }
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setTokens: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
    },
  },
});

export const { login, logout, refreshToken, updateUser, setTokens } =
  authSlice.actions;

// Helper function to get role names as array
export const getUserRoleNames = (user: User | null): string[] => {
  if (!user || !user.roles) return [];
  return user.roles.map((role) =>
    typeof role === "string" ? role : role.name
  );
};

// Helper function to get permission names as array
export const getUserPermissionNames = (user: User | null): string[] => {
  if (!user || !user.permissions) return [];
  return user.permissions.map((permission) =>
    typeof permission === "string" ? permission : permission.name
  );
};

// Helper function to check if user has a specific permission
export const hasPermission = (
  user: User | null,
  permission: string
): boolean => {
  const permissions = getUserPermissionNames(user);
  return permissions.includes(permission);
};

// Helper function to check if user has any of the specified permissions
export const hasAnyPermission = (
  user: User | null,
  permissions: string[]
): boolean => {
  const userPermissions = getUserPermissionNames(user);
  return permissions.some((permission) => userPermissions.includes(permission));
};

// Helper function to check if user has a specific role
export const hasRole = (user: User | null, role: string): boolean => {
  const roles = getUserRoleNames(user);
  return roles.includes(role);
};

export type { User, Role, Permission };
export default authSlice.reducer;
